<template>
  <v-dialog v-model="showDialog" max-width="1300px" scrollable content-class="workload-drill-in-dialog">
    <v-card v-if="data" class="drill-in-card">
      <div class="pa-4 pb-0 flex-shrink-0">
        <div class="d-flex justify-space-between align-start mb-4">
          <div>
            <span class="text-h6 primary--text font-weight-bold">{{ formatDate(data.date) }}</span>
            <span class="caption grey--text ml-2">All Locations · gate-out movements</span>
          </div>
          <div class="d-flex align-center">
            <v-btn
              small
              outlined
              color="primary"
              class="mr-2"
              :disabled="!data.lanes || !data.lanes.length"
              @click="exportXlsx"
            >
              <v-icon small left>mdi-download</v-icon>
              Export
            </v-btn>
            <v-btn icon @click="closeDialog">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </div>

        <div class="d-flex flex-wrap mb-4" style="gap: 12px">
          <v-card outlined class="stat-tile">
            <v-card-text class="py-2 px-3">
              <div class="overline grey--text">Total Moved</div>
              <div class="text-h6 font-weight-bold">{{ data.total_moved }}</div>
            </v-card-text>
          </v-card>
          <v-card outlined class="stat-tile">
            <v-card-text class="py-2 px-3">
              <div class="overline grey--text">Internal</div>
              <div class="text-h6 font-weight-bold success--text">{{ data.internal }}</div>
            </v-card-text>
          </v-card>
          <v-card outlined class="stat-tile">
            <v-card-text class="py-2 px-3">
              <div class="overline grey--text">External</div>
              <div class="text-h6 font-weight-bold warning--text">{{ data.external }}</div>
            </v-card-text>
          </v-card>
          <v-card outlined class="stat-tile">
            <v-card-text class="py-2 px-3">
              <div class="overline grey--text">Lanes</div>
              <div class="text-h6 font-weight-bold">{{ data.lanes_count }}</div>
            </v-card-text>
          </v-card>
          <v-card outlined class="stat-tile">
            <v-card-text class="py-2 px-3">
              <div class="overline grey--text">Revenue</div>
              <div class="text-h6 font-weight-bold success--text">{{ formatCurrency(data.revenue) }}</div>
            </v-card-text>
          </v-card>
          <v-card outlined class="stat-tile">
            <v-card-text class="py-2 px-3">
              <div class="overline grey--text">Cost</div>
              <div class="text-h6 font-weight-bold error--text">{{ formatCurrency(data.cost) }}</div>
            </v-card-text>
          </v-card>
          <v-card outlined class="stat-tile">
            <v-card-text class="py-2 px-3">
              <div class="overline grey--text">Margin</div>
              <div class="text-h6 font-weight-bold">{{ formatCurrency(data.margin) }}</div>
            </v-card-text>
          </v-card>
        </div>
      </div>

      <v-divider />

      <div class="lanes-scroll pa-4">
        <div v-for="(lane, i) in data.lanes" :key="i" class="mb-2">
          <div class="lane-row d-flex align-center justify-space-between" @click="toggleLane(i)">
            <div class="d-flex align-center">
              <v-icon small color="primary" class="mr-2">{{ expandedLanes.includes(i) ? "mdi-chevron-down" : "mdi-chevron-right" }}</v-icon>
              <span class="font-weight-bold">{{ lane.origin }} → {{ lane.destination }}</span>
            </div>
            <div class="d-flex align-center caption" style="gap: 12px">
              <span class="success--text">{{ lane.internal }} int</span>
              <span class="warning--text">{{ lane.external }} ext</span>
              <span class="font-weight-bold">{{ lane.total }}</span>
            </div>
          </div>

          <div v-if="expandedLanes.includes(i)" class="lane-detail pa-3">
            <div class="mb-2">
              <v-chip
                v-for="type in lane.container_types"
                :key="type.type_name"
                x-small
                outlined
                class="mr-2"
              >
                {{ type.type_name }} {{ type.total }} ✓{{ type.completed }}
              </v-chip>
            </div>
            <div
              class="detail-table-wrap"
              @scroll="onLaneTableScroll($event, lane, i)"
            >
              <table class="detail-table">
                <thead>
                  <tr>
                    <th>Container</th>
                    <th>DA</th>
                    <th>Trip</th>
                    <th>Driver</th>
                    <th>Vehicle</th>
                    <th>Type</th>
                    <th>Subhire</th>
                    <th>Vendor</th>
                    <th class="text-right">Revenue</th>
                    <th class="text-right">Cost</th>
                    <th class="text-right">Margin</th>
                    <th>Gate Out</th>
                    <th>Gate In</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(container, ci) in visibleContainers(lane, i)" :key="ci">
                    <td class="font-weight-bold">{{ container.container_number }}</td>
                    <td class="primary--text">{{ container.da_no || "-" }}</td>
                    <td>{{ container.trip_id || "-" }}</td>
                    <td>{{ container.driver_name || "-" }}</td>
                    <td>{{ container.vehicle_number || "-" }}</td>
                    <td><v-chip x-small outlined>{{ container.container_type_name }}</v-chip></td>
                    <td>
                      <v-chip x-small :color="container.is_subhire ? 'warning' : 'grey lighten-2'" :text-color="container.is_subhire ? 'white' : 'black'">
                        {{ container.is_subhire ? "Yes" : "No" }}
                      </v-chip>
                    </td>
                    <td>{{ container.vendor_name || "—" }}</td>
                    <td class="text-right success--text">{{ formatCurrency(container.revenue) }}</td>
                    <td class="text-right error--text">{{ formatCurrency(container.cost) }}</td>
                    <td class="text-right">{{ formatCurrency(container.margin) }}</td>
                    <td>{{ formatDateTime(container.gate_out) }}</td>
                    <td>{{ formatDateTime(container.gate_in) }}</td>
                  </tr>
                </tbody>
              </table>
              <div
                v-if="visibleContainers(lane, i).length < lane.containers.length"
                class="load-more-hint py-2 text-center caption grey--text"
              >
                Scroll for more · {{ visibleContainers(lane, i).length }} of {{ lane.containers.length }}
              </div>
            </div>
          </div>
        </div>
        <div v-if="!data.lanes || !data.lanes.length" class="pa-6 text-center caption grey--text">
          No gate-out movements for this date.
        </div>
      </div>
    </v-card>
    <v-card v-else-if="loading">
      <v-card-text class="pa-6 text-center">
        <v-progress-circular indeterminate color="primary" />
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "WorkloadDrillInDialog",
  props: {
    value: { type: Boolean, default: false },
    date: { type: String, default: null },
    locationCode: { type: String, default: null },
  },
  watch: {
    value(val) {
      if (val && this.date) {
        this.fetchDrillIn();
      } else if (!val) {
        this.data = null;
        this.expandedLanes = [];
        this.laneLoadedCounts = {};
      }
    },
  },
  data() {
    return {
      data: null,
      loading: false,
      expandedLanes: [],
      laneLoadedCounts: {},
      pageSize: 25,
    };
  },
  computed: {
    showDialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },
  methods: {
    formatCurrency(value) {
      const num = Number(value || 0);
      const sign = num < 0 ? "-" : "";
      const abs = Math.abs(num);

      if (abs >= 1000000) {
        return `${sign}AED ${(abs / 1000000).toFixed(1)}M`;
      }
      if (abs >= 1000) {
        return `${sign}AED ${(abs / 1000).toFixed(1)}K`;
      }
      return `${sign}AED ${abs.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
    },
    formatDate(value) {
      return this.$globals.formatDate(value);
    },
    formatDateTime(value) {
      return value ? this.$globals.formatDate(value, true) : "-";
    },
    closeDialog() {
      this.showDialog = false;
    },
    toggleLane(index) {
      const idx = this.expandedLanes.indexOf(index);
      if (idx === -1) {
        this.expandedLanes.push(index);
        if (!this.laneLoadedCounts[index]) {
          this.$set(this.laneLoadedCounts, index, this.pageSize);
        }
      } else {
        this.expandedLanes.splice(idx, 1);
      }
    },
    visibleContainers(lane, index) {
      const count = this.laneLoadedCounts[index] || this.pageSize;
      return lane.containers.slice(0, count);
    },
    onLaneTableScroll(event, lane, index) {
      const el = event.target;
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 80;
      if (!nearBottom) return;

      const loaded = this.laneLoadedCounts[index] || this.pageSize;
      if (loaded >= lane.containers.length) return;

      this.$set(this.laneLoadedCounts, index, Math.min(loaded + this.pageSize, lane.containers.length));
    },
    async exportXlsx() {
      if (!this.data || !this.data.lanes) return;

      const exportData = [];
      this.data.lanes.forEach((lane) => {
        lane.containers.forEach((container) => {
          exportData.push({
            "Origin": lane.origin,
            "Destination": lane.destination,
            "Container": container.container_number,
            "DA": container.da_no || "",
            "Trip": container.trip_id || "",
            "Driver": container.driver_name || "",
            "Vehicle": container.vehicle_number || "",
            "Type": container.container_type_name,
            "Subhire": container.is_subhire ? "Yes" : "No",
            "Vendor": container.vendor_name || "",
            "Revenue": container.revenue,
            "Cost": container.cost,
            "Margin": container.margin,
            "Gate Out": container.gate_out || "",
            "Gate In": container.gate_in || "",
          });
        });
      });

      const XLSX = await import("xlsx");
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Workload Drill In");
      XLSX.writeFile(workbook, `workload-drill-in-${this.data.date}.xlsx`);
    },
    fetchDrillIn() {
      this.loading = true;
      this.$api.workload
        .getWorkloadDrillIn(this.date, this.locationCode)
        .then((res) => {
          this.data = res.data || res;
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showToastMessage", {
            message: "Failed to load drill-in details.",
            color: "error",
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.drill-in-card {
  display: flex;
  flex-direction: column;
  max-height: 85vh;
}
.lanes-scroll {
  overflow-y: auto;
  flex: 1 1 auto;
}
.stat-tile {
  min-width: 110px;
  box-shadow: none;
}
.lane-row {
  padding: 12px 16px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 6px 6px 0 0;
  cursor: pointer;
  background: #fafafa;
}
.lane-row:hover {
  background: #f0f0f0;
}
.lane-detail {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-top: none;
  border-radius: 0 0 6px 6px;
}
.detail-table-wrap {
  overflow: auto;
  max-height: 340px;
}
.detail-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  white-space: nowrap;
}
.detail-table th {
  text-align: left;
  padding: 6px 10px;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: rgba(0, 0, 0, 0.5);
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 1;
}
.detail-table td {
  padding: 8px 10px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}
.detail-table tr:last-child td {
  border-bottom: none;
}
.load-more-hint {
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}
</style>

<style>
.workload-drill-in-dialog {
  max-height: 90vh;
}
</style>
